import * as Sentry from '@sentry/react'

import {
  ABORT_ERROR_MESSAGE_PATTERN,
  ABORT_ERROR_TYPE,
  APOLLO_ERROR_TYPE,
  GRAPHQL_ERROR_MESSAGE,
  UNHANDLED_REJECTION_MECHANISM,
} from './constants'

type HeadersLike = Headers | Record<string, string> | Array<[string, string]> | undefined

/** Minimal shape of a Sentry exception entry — what we read off `event.exception.values[0]`. */
type SentryExceptionEntry = {
  type?: string
  value?: string
  mechanism?: { type?: string; handled?: boolean }
}

/**
 * Benign AbortError noise from in-flight HTTP/GraphQL requests cancelled on navigation/unmount.
 * Scoped to *unhandled* rejections only: a deliberately captured AbortError (handled) still
 * reaches Sentry.
 */
function isBenignAbortError(entry: SentryExceptionEntry | undefined): boolean {
  const mechanism = entry?.mechanism
  const isUnhandled =
    mechanism?.handled === false || mechanism?.type === UNHANDLED_REJECTION_MECHANISM
  const isAbortError =
    entry?.type === ABORT_ERROR_TYPE || ABORT_ERROR_MESSAGE_PATTERN.test(entry?.value ?? '')
  return isUnhandled && isAbortError
}

export function hasHeader(headers: HeadersLike, name: string): boolean {
  if (!headers) return false
  const lower = name.toLowerCase()
  if (headers instanceof Headers) {
    return headers.has(name)
  }
  if (Array.isArray(headers)) {
    for (const [key] of headers) {
      if (key.toLowerCase() === lower) return true
    }
    return false
  }
  if (typeof headers === 'object') {
    for (const key of Object.keys(headers)) {
      if (key.toLowerCase() === lower) return true
    }
  }
  return false
}

export function getRequestUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input
  // Guard for environments where Request/URL may be undefined (e.g., some test runners)
  type GlobalCtors = typeof globalThis & { Request?: typeof Request; URL?: typeof URL }
  const g = globalThis as GlobalCtors
  const hasRequestCtor = typeof g.Request !== 'undefined'
  const hasURLCtor = typeof g.URL !== 'undefined'
  if (hasRequestCtor && g.Request && input instanceof g.Request) return (input as Request).url
  if (hasURLCtor && g.URL && input instanceof g.URL) return (input as URL).toString()
  return String(input)
}

export function getRequestMethod(input: RequestInfo | URL, init?: RequestInit): string {
  type GlobalCtors = typeof globalThis & { Request?: typeof Request }
  const g = globalThis as GlobalCtors
  const hasRequestCtor = typeof g.Request !== 'undefined'
  if (hasRequestCtor && g.Request && input instanceof g.Request) return (input as Request).method
  return init?.method ?? 'GET'
}

export function getRequestHeaders(input: RequestInfo | URL, init?: RequestInit): HeadersLike {
  type GlobalCtors = typeof globalThis & { Request?: typeof Request }
  const g = globalThis as GlobalCtors
  const hasRequestCtor = typeof g.Request !== 'undefined'
  if (hasRequestCtor && g.Request && input instanceof g.Request) return (input as Request).headers
  return init?.headers as HeadersLike
}

/**
 * Error message patterns that originate outside nodafi_web and should never reach Sentry.
 * Each entry is matched by Sentry against the event message / exception value.
 */
export const IGNORED_ERROR_PATTERNS: RegExp[] = [
  // Password manager extensions (LastPass, Bitwarden, etc.) reject promises with this message when they can't find form elements to auto-fill.
  /Object Not Found Matching Id/,
  // Snapchat's iOS in-app browser injects a script referencing its (misspelled) native bridge before our app boots, throwing at HTML line 1. Pure third-party webview noise.
  /SCDynimacBridge/,
  /SCDynamicBridge/, // correct spelling — belt-and-suspenders
]

const NON_REPORTING_HOSTS = ['api.segment.io', 'segment.com', 'posthog.com']

function isNonReportingHost(url: string): boolean {
  try {
    const hostname = new URL(url).hostname
    return NON_REPORTING_HOSTS.some((host) => hostname === host || hostname.endsWith(`.${host}`))
  } catch {
    return false
  }
}

export function setupSentryMonitoring(): void {
  if (process.env.NODE_ENV !== 'production') {
    return
  }

  Sentry.init({
    dsn: 'https://d600de818c7e427f9b8cdde5983484d5@o174923.ingest.sentry.io/5343478',
    environment: process.env.REACT_APP_TARGET_ENV,
    release: process.env.REACT_APP_VERSION,
    ignoreErrors: IGNORED_ERROR_PATTERNS,
    beforeSend(event) {
      const exceptionEntry = event.exception?.values?.[0]
      const type = exceptionEntry?.type ?? ''
      const value = exceptionEntry?.value ?? ''

      // Drop benign abort noise before any grouping logic runs.
      if (isBenignAbortError(exceptionEntry)) {
        return null
      }

      if (event.fingerprint) {
        return event
      }
      if (type === APOLLO_ERROR_TYPE || value === GRAPHQL_ERROR_MESSAGE) {
        return { ...event, fingerprint: ['apollo-error', type, value] }
      }
      return event
    },
  })

  // Global fetch wrapper: report any non-Apollo 4xx/5xx responses to Sentry
  const originalFetch: typeof window.fetch = window.fetch.bind(window)

  window.fetch = async (...args: Parameters<typeof fetch>): Promise<Response> => {
    const [input, init] = args
    const url = getRequestUrl(input)
    const method = getRequestMethod(input, init)
    // Identify Apollo-originated GraphQL requests so we can avoid double-reporting
    const headers = getRequestHeaders(input, init)
    const isApolloGraphQL = hasHeader(headers, 'Apollo-Require-Preflight')
    try {
      const response = await originalFetch(...args)
      try {
        if (
          (response.status >= 400 || response.status === 0) &&
          isApolloGraphQL === false &&
          !isNonReportingHost(url)
        ) {
          Sentry.withScope((scope) => {
            if (response.status === 0) {
              scope.setLevel('error')
              scope.setTag('cors', 'blocked_or_opaque')
            } else {
              scope.setLevel(response.status >= 500 ? 'error' : 'warning')
            }
            scope.setTag('http', 'fetch')
            scope.setExtra('url', url)
            scope.setExtra('method', method)
            scope.setExtra('status', response.status)
            scope.setExtra('statusText', response.statusText)
            Sentry.captureException(
              new Error(
                response.status === 0
                  ? `HTTP status 0 (CORS/network blocked) at ${url}`
                  : `${response.status >= 500 ? 'HTTP server error' : 'HTTP client error'} ${
                      response.status
                    } at ${url}`,
              ),
            )
          })
        }
      } catch {
        // best-effort reporting; never break fetch
      }
      return response
    } catch (err: unknown) {
      try {
        if (isApolloGraphQL === false && !isNonReportingHost(url)) {
          Sentry.addBreadcrumb({
            type: 'http',
            category: 'fetch',
            level: 'error',
            data: {
              url,
              method,
              reason: err instanceof Error ? err.message : String(err),
            },
          })
        }
      } catch {
        // best-effort breadcrumb; never break fetch
      }
      return Promise.reject(err)
    }
  }
}
